import { usePermissions } from "../../../server/auth/use-permissions";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import {
  SupabaseProgramService,
  SupabaseUsedProgramInfoService,
  programsOrder,
} from "../../programs/server/supabase-program-service";

import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import type { Program, UsedProgramInfo } from "../../programs/server/supabase-program-service";

export type ProgramsState = UseSupabaseLoaderState<{
  editable: Program[];
  viewable: Program[];
}> & { reload: () => void };

export const usePrograms = (): ProgramsState => {
  const { ce_admin, super_admin } = usePermissions();

  return useSupabaseLoader(
    async ({ supabase }) => {
      const [{ data }, access] = await Promise.all([
        new SupabaseProgramService(supabase).getAll(null, { order: programsOrder }),
        super_admin ? null : supabase.rpc("program_access_level"),
      ]);

      if (!data) return { editable: [], viewable: [] };
      if (access?.error) throw access.error;

      const grants = access?.data ?? [];
      const editableIds = new Set(grants.filter((a) => a.editable).map(({ id }) => id));
      const viewableIds = new Set(grants.map(({ id }) => id));
      const visible = data.filter((program) => program.visible || ce_admin);

      return {
        editable: visible.filter(({ id }) => super_admin || editableIds.has(id)),
        viewable: visible.filter(({ id }) => super_admin || viewableIds.has(id)),
      };
    },
    [ce_admin, super_admin],
  );
};

export function useUsedProgramsInfo(): ReturnType<typeof useSupabaseLoader<UsedProgramInfo[]>> {
  return useSupabaseLoader(async ({ supabase, account_id }) => {
    const { data, error } = await new SupabaseUsedProgramInfoService(supabase).getAll(account_id, {
      order: [{ column: "id" }],
    });

    if (error || !data) return [];
    return data;
  }, []);
}
